import React, { useRef, useState } from "react";
import Icon from "../shared/Icon";

/**
 * Import CSV, en action secondaire de l'en-tête.
 *
 * Le format attendu n'est plus affiché en permanence : trois lignes de
 * spécification occupaient le haut du panneau alors qu'elles ne servent qu'au
 * moment de préparer un fichier. Elles apparaissent au survol et au focus
 * clavier — pas seulement au survol, sinon le détail devient inatteignable
 * sans souris.
 *
 * `papaparse` est en import dynamique : il n'est chargé qu'au premier fichier
 * choisi, et reste hors du bundle initial.
 */

const REQUIRED_HEADERS = ["User_ID", "First_name", "Last_name", "Residence"];

const CsvImportButton = ({ onImport, onError, t }) => {
  const inputRef = useRef(null);
  const [isReading, setIsReading] = useState(false);

  const handleFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    onError(null);
    setIsReading(true);

    const papa = await import("papaparse");
    const Papa = papa.default || papa;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setIsReading(false);
        event.target.value = "";

        const headers = results.meta.fields || [];
        const missing = REQUIRED_HEADERS.filter((header) => !headers.includes(header));
        if (missing.length > 0) {
          onError(`${t("Missing columns")} : ${missing.join(", ")}`);
          return;
        }

        // Une ligne sans identifiant ou sans nom est ignorée plutôt que de
        // faire échouer l'import : on peut ainsi charger un export brut.
        const rows = results.data
          .filter((row) => row.User_ID && row.Last_name)
          .map((row) => ({
            woshId: row.User_ID,
            firstName: row.First_name || "",
            lastName: row.Last_name,
            residence: row.Residence || "",
            pages: row.Pages || 1,
          }));

        if (rows.length === 0) {
          onError(t("No usable row in this file."));
          return;
        }

        onImport(rows);
      },
      error: () => {
        setIsReading(false);
        event.target.value = "";
        onError(t("Could not read this CSV file."));
      },
    });
  };

  return (
    <div className="mgr-lbl-csv">
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={handleFile}
        className="mgr-lbl-file"
      />

      <button
        type="button"
        className="mgr-btn-ghost mgr-lbl-csv-btn"
        onClick={() => inputRef.current && inputRef.current.click()}
        disabled={isReading}
        aria-describedby="mgr-lbl-csv-help"
      >
        <Icon name="download-outline" style={{ fontSize: 17 }} />
        {isReading ? t("Reading…") : t("Import a CSV")}
      </button>

      <div className="mgr-lbl-csv-help" id="mgr-lbl-csv-help" role="tooltip">
        <div className="mgr-lbl-csv-help-title">{t("First row, exactly:")}</div>
        <code>User_ID,First_name,Last_name,Residence</code>
        <div className="mgr-lbl-csv-help-note">{t("Optional column: Pages.")}</div>
      </div>
    </div>
  );
};

export default CsvImportButton;
